/**
 * useAssessmentSession — Assessment session management hook
 *
 * Encapsulates all assessment-related state and handlers:
 * - Blueprint generation, attempt tracking, report building
 * - Session recovery (crash resilience)
 * - Per-question timing
 *
 * Moved from src/hooks/useAssessmentSession.ts in R32 (DDD Fase 5).
 */

import { useState, useRef, useEffect } from 'react'
import {
  type Question,
  type ModuleId,
  getQuestionWithExplanation,
} from '../../content'
import type { FamilyMasteryState } from '../../mastery'
import { GroupId } from '../../config/systemConfig'
import {
  AssessmentConfig,
  AssessmentBlueprint,
  AssessmentAttempt,
  AssessmentAnswer,
  AssessmentReport,
} from '../types'
import { generateAssessmentBlueprint } from '../utils/assessmentBlueprintGenerator'
import {
  saveBlueprint,
  saveAttempt,
  saveReport,
} from '../utils/assessmentStorage'
import { buildAssessmentReport } from '../utils/assessmentReportBuilder'

import {
  saveSessionState,
  getRecoverableSession,
  clearSessionState,
  RecoverableSession,
} from '../utils/assessmentSessionRecovery'
import { filterSession } from '../../practice/logic/pedagogicalGuard'
import { error as logError, debug } from '../../utils/logger'

interface UseAssessmentSessionParams {
  familyMastery: Record<string, FamilyMasteryState>
  selectedGroup: GroupId
  recordAnswer: (
    module: ModuleId,
    questionId: string,
    isCorrect: boolean,
    userChoice: number,
    payload?: string,
    errorType?: string,
    delta?: number,
  ) => void
  allQuestions: Question[]
  /** Called once on mount if a recoverable session is found */
  isReady: boolean
  /** The ID of the learner taking the assessment */
  learnerId?: string
}

interface AssessmentStartResult {
  questions: Question[]
}

export function useAssessmentSession({
  familyMastery,
  selectedGroup,
  recordAnswer,
  allQuestions,
  isReady,
  learnerId,
}: UseAssessmentSessionParams) {
  // ── State ──────────────────────────────────────────────────────────
  const [assessmentBlueprint, setAssessmentBlueprint] =
    useState<AssessmentBlueprint | null>(null)
  const [assessmentAttempt, setAssessmentAttempt] =
    useState<AssessmentAttempt | null>(null)
  const latestAttemptRef = useRef<AssessmentAttempt | null>(null)
  const [assessmentStartTime, setAssessmentStartTime] = useState<number>(0)
  const [preFamilyMastery, setPreFamilyMastery] = useState<
    Record<string, FamilyMasteryState>
  >({})
  const questionStartTimeRef = useRef<number>(Date.now())
  const [assessmentReport, setAssessmentReport] =
    useState<AssessmentReport | null>(null)
  const [recoverableSession, setRecoverableSession] =
    useState<RecoverableSession | null>(null)
  const [assessmentError, setAssessmentError] = useState<string | null>(null)

  // ── Recovery check on mount ────────────────────────────────────────
  useEffect(() => {
    if (isReady && !assessmentBlueprint) {
      let cancelled = false
      const checkRecovery = async () => {
        const session = await getRecoverableSession()
        if (!cancelled && session) {
          setRecoverableSession(session)
        }
      }
      checkRecovery()
      return () => {
        cancelled = true
      }
    }
  }, [isReady])

  // ── Handlers ───────────────────────────────────────────────────────

  const startAssessment = (
    config: AssessmentConfig,
  ): AssessmentStartResult | null => {
    // Capture pre-assessment mastery state (deep clone)
    let preMastery: Record<string, FamilyMasteryState> = {}
    try {
      preMastery = JSON.parse(JSON.stringify(familyMastery || {}))
    } catch (err) {
      logError('Failed to clone familyMastery:', err)
    }
    setPreFamilyMastery(preMastery)

    // Generate blueprint
    const blueprint = generateAssessmentBlueprint(
      config,
      familyMastery || {},
      selectedGroup,
    )

    if (blueprint.questions.length === 0) {
      setAssessmentError(
        'Geen vragen beschikbaar voor deze configuratie. Probeer andere vakken te selecteren.',
      )
      return null
    }

    // Save blueprint
    saveBlueprint(blueprint)
    setAssessmentBlueprint(blueprint)

    // Create attempt
    const attempt: AssessmentAttempt = {
      id: `attempt_${Date.now()}`,
      blueprintId: blueprint.id,
      startedAt: Date.now(),
      completedAt: null,
      selectedGroup,
      answers: [],
      countsForMastery: config.countsForMastery,
      totalTimeSeconds: 0,
      status: 'in-progress',
      domainId: config.domainId,
      assignmentId: config.assignmentId,
      learnerId,
    }
    setAssessmentAttempt(attempt)
    setAssessmentStartTime(Date.now())
    questionStartTimeRef.current = Date.now()

    // Save initial session state for recovery
    saveSessionState(blueprint, attempt, Date.now(), preMastery)

    // Build questions for PracticeSession
    const rawQuestions = blueprint.questions
      .map((bq) => getQuestionWithExplanation(bq.questionId))
      .filter((q): q is Question => q !== null)

    // Run pedagogical guard — reject fatally invalid questions, replace if possible
    const usedQuestionIds = new Set(rawQuestions.map((q) => q.id))
    const mcReplacementPool = allQuestions.filter(
      (q) => q.questionType === 'multiple_choice' && !usedQuestionIds.has(q.id),
    )
    const { questions: guardedQuestions, stats: guardStats } = filterSession(
      rawQuestions,
      rawQuestions.length,
      mcReplacementPool,
    )

    if (guardStats.fatalRejected > 0) {
      debug(
        `[Assessment] Pedagogical guard: ${guardStats.fatalRejected} fatal rejections, ${guardStats.replaced} replaced`,
      )
    }

    return { questions: guardedQuestions }
  }

  const completeAssessment = (): void => {
    // Read from ref to avoid stale closure — React state may not yet
    // reflect the last handleAnswer call when this runs immediately after.
    const currentAttempt = latestAttemptRef.current || assessmentAttempt
    if (!currentAttempt || !assessmentBlueprint) return

    const totalTimeSeconds = Math.floor(
      (Date.now() - assessmentStartTime) / 1000,
    )

    const completedAttempt: AssessmentAttempt = {
      ...currentAttempt,
      completedAt: Date.now(),
      totalTimeSeconds,
      status: 'completed',
    }
    saveAttempt(completedAttempt)

    const report = buildAssessmentReport(
      completedAttempt,
      assessmentBlueprint,
      preFamilyMastery,
      familyMastery || {},
    )
    saveReport(report)

    setAssessmentReport(report)
    setAssessmentBlueprint(null)
    setAssessmentAttempt(null)
    latestAttemptRef.current = null
    clearSessionState()
  }

  const handleAnswer = (
    questionId: string,
    isCorrect: boolean,
    userChoice: number,
    payload?: string,
    errorType?: string,
    delta?: number,
  ): void => {
    if (!assessmentAttempt || !assessmentBlueprint) return

    const question = allQuestions.find((q) => q.id === questionId)
    if (!question) return

    const blueprintQuestion = assessmentBlueprint.questions.find(
      (bq) => bq.questionId === questionId,
    )
    if (!blueprintQuestion) return

    // Calculate time spent using per-question timer
    const now = Date.now()
    const timeSpent = Math.floor((now - questionStartTimeRef.current) / 1000)
    questionStartTimeRef.current = now

    const answer: AssessmentAnswer = {
      questionId,
      userChoice,
      isCorrect,
      timeSpent: Math.max(1, timeSpent),
      familyId: blueprintQuestion.familyId,
      module: blueprintQuestion.module,
    }

    const updatedAttempt = {
      ...assessmentAttempt,
      answers: [...assessmentAttempt.answers, answer],
    }
    setAssessmentAttempt(updatedAttempt)
    latestAttemptRef.current = updatedAttempt

    // Persist for crash recovery
    saveSessionState(
      assessmentBlueprint,
      updatedAttempt,
      assessmentStartTime,
      preFamilyMastery,
    )

    // Optionally update mastery
    if (assessmentAttempt.countsForMastery) {
      recordAnswer(
        question.module,
        questionId,
        isCorrect,
        userChoice,
        payload,
        errorType,
        delta,
      )
    }
  }

  const resumeAssessment = (): AssessmentStartResult | null => {
    if (!recoverableSession) return null

    const {
      blueprint,
      attempt,
      startTime,
      preFamilyMastery: preMastery,
    } = recoverableSession

    setAssessmentBlueprint(blueprint)
    setAssessmentAttempt(attempt)
    setAssessmentStartTime(startTime)
    setPreFamilyMastery(preMastery)

    // Build remaining questions (skip already answered)
    const answeredIds = new Set(attempt.answers.map((a) => a.questionId))
    const remainingQuestions = blueprint.questions
      .filter((bq) => !answeredIds.has(bq.questionId))
      .map((bq) => getQuestionWithExplanation(bq.questionId))
      .filter((q): q is Question => q !== null)

    setRecoverableSession(null)

    if (remainingQuestions.length === 0) {
      clearSessionState()
      return null
    }

    return { questions: remainingQuestions }
  }

  const abandonRecovery = (): void => {
    if (recoverableSession) {
      const abandonedAttempt: AssessmentAttempt = {
        ...recoverableSession.attempt,
        completedAt: Date.now(),
        totalTimeSeconds: Math.floor(
          (Date.now() - recoverableSession.startTime) / 1000,
        ),
        status: 'abandoned',
      }
      saveAttempt(abandonedAttempt)
    }
    clearSessionState()
    setRecoverableSession(null)
  }

  const abandonCurrentAssessment = (): void => {
    if (assessmentAttempt) {
      const abandonedAttempt: AssessmentAttempt = {
        ...assessmentAttempt,
        completedAt: Date.now(),
        totalTimeSeconds: Math.floor((Date.now() - assessmentStartTime) / 1000),
        status: 'abandoned',
      }
      saveAttempt(abandonedAttempt)
      clearSessionState()
    }
    setAssessmentBlueprint(null)
    setAssessmentAttempt(null)
  }

  const clearAssessmentState = (): void => {
    setAssessmentBlueprint(null)
    setAssessmentAttempt(null)
    setAssessmentReport(null)
    setAssessmentError(null)
  }

  return {
    // State
    assessmentBlueprint,
    assessmentAttempt,
    assessmentReport,
    assessmentError,
    recoverableSession,
    assessmentStartTime,

    // Handlers
    startAssessment,
    completeAssessment,
    handleAnswer,
    resumeAssessment,
    abandonRecovery,
    abandonCurrentAssessment,
    clearAssessmentState,

    // Setters (for modals)
    setAssessmentError,
    setAssessmentReport,
  }
}
